import React, { useState, useEffect, useCallback, lazy, Suspense, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { itemsAPI, userAPI, zohoAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatNumber } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import ZohoSyncStamp from '../components/layout/ZohoSyncStamp';
import { toast } from 'sonner';
import {
  Package, Search, Loader2, RefreshCw, TrendingUp,
  Truck, ArrowDownToLine, ArrowUpDown, ArrowUp, ArrowDown, UserPlus, Users, X, Check,
  TrendingDown, Minus, AlertTriangle, CheckCircle, ShieldAlert, Clock, Bell
} from 'lucide-react';
import AssignItemsModal from '../components/items/AssignItemsModal';

const ItemHistoryModal = lazy(() => import('../components/items/ItemHistoryModal'));

const PERIOD_OPTIONS = [
  { value: '14', label: '14 Days', days: 14 },
  { value: '30', label: '1 Month', days: 30 },
  { value: '90', label: '3 Months', days: 90 },
  { value: '180', label: '6 Months', days: 180 },
  { value: '365', label: '1 Year', days: 365 },
  { value: '0', label: 'All Time', days: 0 },
];

function TrendArrow({ trend, tooltipPrefix = '', currentValue }) {
  if (!trend) return <span className="text-muted-foreground">—</span>;
  const { pct, arrow, prev } = trend;
  const isUp = arrow === 'up';
  const isDown = arrow === 'down';
  const color = isUp ? 'text-emerald-600' : isDown ? 'text-red-600' : 'text-muted-foreground';
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
  const sign = pct >= 0 ? '+' : '';

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex items-center gap-0.5 text-xs font-mono ${color}`} data-testid={`trend-${tooltipPrefix}`}>
            <Icon className="h-3 w-3" />
            {sign}{pct}%
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs max-w-[220px]">
          <p><strong>{tooltipPrefix} Trend</strong></p>
          <p>Current: ${currentValue?.toFixed(2) || '—'}</p>
          <p>Previous: ${prev?.toFixed(2) || '—'}</p>
          <p>Change: {sign}{pct}%</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function PriceCell({ value, qty, txCount, label, period }) {
  if (value == null) return <span className="text-muted-foreground">—</span>;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="font-mono text-sm" data-testid={`price-${label}`}>
            ${value.toFixed(2)}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs max-w-[200px]">
          <p><strong>{label}</strong> ({period})</p>
          <p>Transactions: {txCount}</p>
          <p>Total qty: {formatNumber(qty)}</p>
          <p>Weighted avg (USD)</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function ItemsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [pmFilter, setPmFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || 'all');
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [periodDays, setPeriodDays] = useState('90');
  const [analytics, setAnalytics] = useState({});
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [riskAlerts, setRiskAlerts] = useState({});
  const [riskSummary, setRiskSummary] = useState(null);
  const [agingData, setAgingData] = useState({});
  const [ageSort, setAgeSort] = useState(null); // null, 'asc', 'desc'
  const limit = 50;

  const [selectedItems, setSelectedItems] = useState(new Set());
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [productManagers, setProductManagers] = useState([]);
  const [selectedPM, setSelectedPM] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historySku, setHistorySku] = useState(null);

  const { canAccess, isAdmin, hasRole } = useAuth();
  const canAssignItems = canAccess('ITEMS', 'EDIT');
  // F4 Step 6 (1 Nov 2026): kept on hasRole('product_manager') deliberately — see
  // App.js's PriceAnalyticsRoute comment and OPEN_QUESTIONS_FOR_LEX.md.
  const hasPriceAnalyticsAccess = isAdmin || hasRole('product_manager');

  const fetchSummary = useCallback(async () => { try { const r = await itemsAPI.getSummary(); setSummary(r.data); } catch (e) {} }, []);

  const fetchCategories = useCallback(async () => {
    try { const r = await zohoAPI.getItemCategories(); setCategories(r.data.categories || []); } catch (e) {}
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit, ...(search && { search }), ...(assignmentFilter !== 'all' && { assignment_filter: assignmentFilter }), ...(pmFilter !== 'all' && { pm_filter: pmFilter }), ...(categoryFilter !== 'all' && { category: categoryFilter }), sort_field: sortField, sort_order: sortOrder };
      const r = await itemsAPI.list(params);
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    } catch (e) {} finally { setLoading(false); }
  }, [page, search, assignmentFilter, pmFilter, categoryFilter, sortField, sortOrder]);

  const fetchAnalytics = useCallback(async () => {
    if (!hasPriceAnalyticsAccess) { setAnalyticsLoading(false); return; }
    setAnalyticsLoading(true);
    try {
      const [analyticsRes, riskRes, agingRes] = await Promise.all([
        itemsAPI.getPriceAnalytics(parseInt(periodDays)),
        itemsAPI.getRiskAlerts(),
        itemsAPI.getAgingSummary(),
      ]);
      setAnalytics(analyticsRes.data.analytics || {});
      setRiskAlerts(riskRes.data.alerts || {});
      setRiskSummary(riskRes.data.summary || null);
      setAgingData(agingRes.data.aging || {});
    } catch (e) {
      console.error('Failed to load analytics', e);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [periodDays, hasPriceAnalyticsAccess]);

  const fetchProductManagers = useCallback(async () => { try { const r = await itemsAPI.getProductManagers(); setProductManagers((r.data || []).filter(u => u.is_active !== false).sort((a,b) => (a.name||'').localeCompare(b.name||''))); } catch (e) {} }, []);

  useEffect(() => { fetchSummary(); fetchCategories(); if (canAssignItems) fetchProductManagers(); }, [fetchSummary, fetchCategories, fetchProductManagers, canAssignItems]);
  useEffect(() => { const t = setTimeout(() => { setPage(0); fetchItems(); }, 300); return () => clearTimeout(t); }, [search, assignmentFilter, pmFilter, categoryFilter, sortField, sortOrder]);
  useEffect(() => { fetchItems(); }, [page, fetchItems]);
  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);
  useEffect(() => {
    setSearchParams(categoryFilter !== 'all' ? { category: categoryFilter } : {}, { replace: true });
  }, [categoryFilter, setSearchParams]);

  const handleRefresh = async () => { setSelectedItems(new Set()); await Promise.all([fetchSummary(), fetchItems(), fetchAnalytics()]); };
  const handleSort = (field) => { if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); else { setSortField(field); setSortOrder('asc'); } setAgeSort(null); };
  const toggleItemSelection = (sku) => { setSelectedItems(prev => { const n = new Set(prev); if (n.has(sku)) n.delete(sku); else n.add(sku); return n; }); };
  const selectAllOnPage = () => { setSelectedItems(prev => { const n = new Set(prev); items.forEach(item => { if (item.sku) n.add(item.sku); }); return n; }); };
  const clearSelection = () => { setSelectedItems(new Set()); };

  const [conflictData, setConflictData] = useState(null);
  const [unassignConfirm, setUnassignConfirm] = useState(false);

  const handleAssignItems = async () => {
    if (!selectedPM || selectedItems.size === 0) { toast.error('Please select a user and items'); return; }
    setAssigning(true);
    try {
      // Check for conflicts first
      const checkRes = await itemsAPI.checkAssignmentConflicts({ item_skus: Array.from(selectedItems), product_manager_id: selectedPM });
      if (checkRes.data.has_conflicts) {
        setConflictData(checkRes.data.conflicts);
        setAssigning(false);
        return;
      }
      // No conflicts — assign directly
      await doAssign();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); setAssigning(false); }
  };

  const doAssign = async () => {
    setAssigning(true);
    try {
      const r = await itemsAPI.assign({ item_skus: Array.from(selectedItems), product_manager_id: selectedPM });
      toast.success(r.data.message); setAssignModalOpen(false); setSelectedItems(new Set()); setSelectedPM(''); setConflictData(null); fetchItems();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to assign items'); }
    finally { setAssigning(false); }
  };

  const handleUnassignItems = async () => {
    if (selectedItems.size === 0) { toast.error('Please select items to unassign'); return; }
    setUnassignConfirm(true);
  };

  const doUnassign = async () => {
    setUnassignConfirm(false);
    setAssigning(true);
    try {
      const r = await itemsAPI.unassign(Array.from(selectedItems));
      toast.success(r.data.message || 'Items unassigned successfully');
      setSelectedItems(new Set());
      fetchItems();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to unassign items'); }
    finally { setAssigning(false); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 ml-1 text-primary" /> : <ArrowDown className="h-3 w-3 ml-1 text-primary" />;
  };
  const SortableHeader = ({ field, children, className = '' }) => (
    <TableHead className={`font-medium text-xs uppercase cursor-pointer hover:bg-muted/80 select-none ${className}`} onClick={() => handleSort(field)}>
      <div className="flex items-center">{children}<SortIcon field={field} /></div>
    </TableHead>
  );

  const totalPages = Math.ceil(total / limit);
  const selectedCount = selectedItems.size;
  const periodLabel = PERIOD_OPTIONS.find(p => p.value === periodDays)?.label || '14 Days';

  // Client-side sort by age
  const sortedItems = ageSort ? [...items].sort((a, b) => {
    const ageA = agingData[a.sku]?.avg_age_days ?? -1;
    const ageB = agingData[b.sku]?.avg_age_days ?? -1;
    return ageSort === 'asc' ? ageA - ageB : ageB - ageA;
  }) : items;

  const handleAgeSort = () => {
    if (!ageSort) setAgeSort('desc');
    else if (ageSort === 'desc') setAgeSort('asc');
    else setAgeSort(null);
  };

  const AgeSortIcon = () => {
    if (!ageSort) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return ageSort === 'asc' ? <ArrowUp className="h-3 w-3 ml-1 text-primary" /> : <ArrowDown className="h-3 w-3 ml-1 text-primary" />;
  };

  return (
    <div className="space-y-6" data-testid="items-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Package className="h-6 w-6 text-primary" />Items</h1>
          <p className="text-muted-foreground">Live inventory from Zoho ({formatNumber(total)} items)</p>
          <ZohoSyncStamp className="mt-1" entity="items" />
        </div>
        <div className="flex gap-2">
          {canAssignItems && selectedCount > 0 && (
            <>
              <Button variant="outline" onClick={() => setAssignModalOpen(true)} className="rounded-sm" data-testid="assign-items-btn"><UserPlus className="h-4 w-4 mr-2" />Assign ({selectedCount})</Button>
              <Button variant="outline" onClick={handleUnassignItems} disabled={assigning} className="rounded-sm" data-testid="unassign-items-btn"><X className="h-4 w-4 mr-2" />Unassign</Button>
            </>
          )}
          <Button variant="outline" onClick={handleRefresh} disabled={loading} className="rounded-sm" data-testid="refresh-btn"><RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
          {canAccess('RECONCILE', 'VIEW') && (
            <Button variant="outline" disabled title="Stock risk notifications are disabled while stock sync is restricted" className="rounded-sm opacity-50 cursor-not-allowed" data-testid="generate-risk-notifs-btn"><Bell className="h-4 w-4 mr-2" />Alert PMs (Disabled)</Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="rounded-sm"><CardContent className="p-4"><div className="flex items-center gap-2 text-muted-foreground mb-1"><Package className="h-4 w-4" /><span className="text-xs font-medium uppercase">Total Items</span></div><p className="text-2xl font-bold font-mono">{formatNumber(summary.total_items)}</p></CardContent></Card>
          <Card className="rounded-sm"><CardContent className="p-4"><div className="flex items-center gap-2 text-blue-600 mb-1"><TrendingUp className="h-4 w-4" /><span className="text-xs font-medium uppercase">On Hand</span><Badge variant="outline" className="text-[9px] px-1 py-0 rounded-sm text-muted-foreground border-muted-foreground/30">Not in use</Badge></div><p className="text-2xl font-bold font-mono text-blue-600/50">{formatNumber(summary.total_stock_on_hand)}</p></CardContent></Card>
          <Card className="rounded-sm"><CardContent className="p-4"><div className="flex items-center gap-2 text-amber-600 mb-1"><ArrowDownToLine className="h-4 w-4" /><span className="text-xs font-medium uppercase">Committed</span><Badge variant="outline" className="text-[9px] px-1 py-0 rounded-sm text-muted-foreground border-muted-foreground/30">Not in use</Badge></div><p className="text-2xl font-bold font-mono text-amber-600/50">{formatNumber(summary.total_committed_stock)}</p></CardContent></Card>
          <Card className="rounded-sm"><CardContent className="p-4"><div className="flex items-center gap-2 text-purple-600 mb-1"><Truck className="h-4 w-4" /><span className="text-xs font-medium uppercase">To Receive</span><Badge variant="outline" className="text-[9px] px-1 py-0 rounded-sm text-muted-foreground border-muted-foreground/30">Not in use</Badge></div><p className="text-2xl font-bold font-mono text-purple-600/50">{formatNumber(summary.total_to_be_received)}</p></CardContent></Card>
          {riskSummary && riskSummary.at_risk + riskSummary.critical > 0 && (
            <Card className="rounded-sm border-muted"><CardContent className="p-4"><div className="flex items-center gap-2 text-muted-foreground mb-1"><ShieldAlert className="h-4 w-4" /><span className="text-xs font-medium uppercase">At Risk</span><Badge variant="outline" className="text-[9px] px-1 py-0 rounded-sm text-muted-foreground border-muted-foreground/30">Not in use</Badge></div><p className="text-2xl font-bold font-mono text-muted-foreground">{riskSummary.at_risk + riskSummary.critical}</p><p className="text-xs text-muted-foreground">{riskSummary.critical} critical</p></CardContent></Card>
          )}
          {riskSummary && (
            <Card className="rounded-sm"><CardContent className="p-4"><div className="flex items-center gap-2 text-muted-foreground mb-1"><CheckCircle className="h-4 w-4" /><span className="text-xs font-medium uppercase">Safe</span><Badge variant="outline" className="text-[9px] px-1 py-0 rounded-sm text-muted-foreground border-muted-foreground/30">Not in use</Badge></div><p className="text-2xl font-bold font-mono text-muted-foreground">{riskSummary.safe}</p><p className="text-xs text-muted-foreground">of {riskSummary.total_skus_with_demand} with demand</p></CardContent></Card>
          )}
        </div>
      )}

      {/* Filters */}
      <Card className="rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by SKU, name, brand..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 rounded-sm" data-testid="search-input" />
            </div>
            {/* Average Period Selector */}
            <Select value={periodDays} onValueChange={setPeriodDays}>
              <SelectTrigger className="w-40 rounded-sm" data-testid="period-selector">
                <SelectValue placeholder="Avg Period" />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canAssignItems && (
              <Select value={assignmentFilter} onValueChange={setAssignmentFilter}>
                <SelectTrigger className="w-36 rounded-sm" data-testid="assignment-filter"><SelectValue placeholder="Assignment" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Items</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                </SelectContent>
              </Select>
            )}
            {categories.length > 0 && (
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-48 rounded-sm" data-testid="category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c.name} value={c.name}>{c.name} ({c.count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {canAssignItems && productManagers.length > 0 && (
              <Select value={pmFilter} onValueChange={setPmFilter}>
                <SelectTrigger className="w-44 rounded-sm" data-testid="pm-filter"><SelectValue placeholder="Product Manager" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All PMs</SelectItem>
                  {productManagers.map(pm => (
                    <SelectItem key={pm.id} value={pm.id}>{pm.name || pm.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {canAssignItems && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAllOnPage} className="rounded-sm" data-testid="select-page-btn">Select Page</Button>
                <Button variant="outline" size="sm" onClick={async () => {
                  try {
                    const params = { limit: 10000, ...(search && { search }), ...(assignmentFilter !== 'all' && { assignment_filter: assignmentFilter }), ...(pmFilter !== 'all' && { pm_filter: pmFilter }), ...(categoryFilter !== 'all' && { category: categoryFilter }) };
                    const r = await itemsAPI.list(params);
                    const allSkus = (r.data.items || []).map(i => i.sku).filter(Boolean);
                    setSelectedItems(new Set(allSkus));
                    toast.success(`Selected ${allSkus.length} items`);
                  } catch(e) { toast.error('Failed to select all'); }
                }} className="rounded-sm" data-testid="select-all-filtered-btn">Select All ({total})</Button>
                {selectedCount > 0 && <Button variant="ghost" size="sm" onClick={clearSelection} className="rounded-sm">Clear ({selectedCount})</Button>}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="rounded-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                {canAssignItems && <TableHead className="w-10 font-medium text-xs uppercase"><span className="sr-only">Select</span></TableHead>}
                <SortableHeader field="sku">SKU</SortableHeader>
                <SortableHeader field="name" className="min-w-[180px]">Name</SortableHeader>
                <TableHead className="font-medium text-xs uppercase">Category</TableHead>
                <SortableHeader field="stock_on_hand">On Hand <span className="text-[9px] text-muted-foreground font-normal">(not in use)</span></SortableHeader>
                <SortableHeader field="available_stock">Available <span className="text-[9px] text-muted-foreground font-normal">(not in use)</span></SortableHeader>
                <TableHead className="font-medium text-xs uppercase text-right">Avg Buy</TableHead>
                <TableHead className="font-medium text-xs uppercase text-center w-20">Trend</TableHead>
                <TableHead className="font-medium text-xs uppercase text-right">Avg Sell</TableHead>
                <TableHead className="font-medium text-xs uppercase text-center w-20">Trend</TableHead>
                <TableHead className="font-medium text-xs uppercase text-right">Spread</TableHead>
                <TableHead className="font-medium text-xs uppercase text-center w-20">Trend</TableHead>
                <TableHead className="font-medium text-xs uppercase text-right">vs Target</TableHead>
                <TableHead className="font-medium text-xs uppercase text-center">Risk <span className="text-[9px] text-muted-foreground font-normal">(not in use)</span></TableHead>
                <TableHead className="font-medium text-xs uppercase text-center cursor-pointer hover:bg-muted/80 select-none" onClick={handleAgeSort} data-testid="age-sort-header">
                  <div className="flex items-center justify-center">Age<AgeSortIcon /></div>
                </TableHead>
                {canAssignItems && <SortableHeader field="assigned_to_name">PM</SortableHeader>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={canAssignItems ? 17 : 15} className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={canAssignItems ? 17 : 15} className="text-center py-8"><Package className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" /><p className="text-muted-foreground">No items found</p></TableCell></TableRow>
              ) : (
                sortedItems.map((item) => {
                  const isSelected = selectedItems.has(item.sku);
                  const a = analytics[item.sku] || {};
                  return (
                    <TableRow key={item.zoho_item_id} className={`table-row-hover cursor-pointer ${isSelected ? 'bg-primary/5' : ''}`} data-testid={`item-row-${item.sku}`}
                      onClick={() => { setHistorySku(item.sku); setHistoryModalOpen(true); }}>
                      {canAssignItems && (
                        <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                          <div className={`h-4 w-4 rounded-sm border shadow flex items-center justify-center cursor-pointer ${isSelected ? 'bg-primary border-primary' : 'border-primary'}`} onClick={() => toggleItemSelection(item.sku)}>
                            {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                          </div>
                        </TableCell>
                      )}
                      <TableCell className="font-mono text-xs font-medium text-primary hover:underline">{item.sku}</TableCell>
                      <TableCell className="max-w-[200px]"><div className="truncate text-sm">{item.name}</div></TableCell>
                      <TableCell>{item.category_name ? <Badge variant="outline" className="text-xs rounded-sm whitespace-nowrap">{item.category_name}</Badge> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                      <TableCell className="font-mono text-right text-sm text-muted-foreground/60">{formatNumber(item.stock_on_hand)}</TableCell>
                      <TableCell className="font-mono text-right text-sm text-muted-foreground/60">{formatNumber(item.available_stock)}</TableCell>
                      {/* Avg Buy */}
                      <TableCell className="text-right">
                        <PriceCell value={a.avg_buy} qty={a.buy_qty} txCount={a.buy_tx} label="Avg Buy" period={periodLabel} />
                      </TableCell>
                      <TableCell className="text-center">
                        <TrendArrow trend={a.buy_trend} tooltipPrefix="Buy" currentValue={a.avg_buy} />
                      </TableCell>
                      {/* Avg Sell */}
                      <TableCell className="text-right">
                        <PriceCell value={a.avg_sell} qty={a.sell_qty} txCount={a.sell_tx} label="Avg Sell" period={periodLabel} />
                      </TableCell>
                      <TableCell className="text-center">
                        <TrendArrow trend={a.sell_trend} tooltipPrefix="Sell" currentValue={a.avg_sell} />
                      </TableCell>
                      {/* Spread */}
                      <TableCell className="text-right">
                        {a.spread != null ? (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className={`font-mono text-sm ${a.spread >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid={`spread-${item.sku}`}>
                                  {a.spread >= 0 ? '+' : ''}{a.spread.toFixed(2)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs">
                                <p>Spread = Avg Sell - Avg Buy</p>
                                <p>${a.avg_sell?.toFixed(2)} - ${a.avg_buy?.toFixed(2)} = ${a.spread.toFixed(2)}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        <TrendArrow trend={a.spread_trend} tooltipPrefix="Spread" currentValue={a.spread} />
                      </TableCell>
                      {/* Spread vs Target */}
                      <TableCell className="text-right">
                        {a.spread_vs_target != null ? (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center gap-1 text-xs font-mono" data-testid={`svt-${item.sku}`}>
                                  {a.spread_vs_target_status === 'above' ? (
                                    <><CheckCircle className="h-3 w-3 text-emerald-600" /><span className="text-emerald-700">+{a.spread_vs_target.toFixed(2)}</span></>
                                  ) : (
                                    <><AlertTriangle className="h-3 w-3 text-amber-600" /><span className="text-amber-700">{a.spread_vs_target.toFixed(2)}</span></>
                                  )}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs max-w-[220px]">
                                <p><strong>Spread vs Target</strong></p>
                                <p>Target buy: ${a.target_price?.toFixed(2) || '—'}</p>
                                <p>Target spread: ${a.avg_sell != null && a.target_price != null ? (a.avg_sell - a.target_price).toFixed(2) : '—'}</p>
                                <p>Actual spread: ${a.spread?.toFixed(2) || '—'}</p>
                                <p>Difference: ${a.spread_vs_target.toFixed(2)}</p>
                                <p>{a.spread_vs_target_status === 'above' ? 'Above target margin' : 'Below target margin'}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      {/* Risk Alert — disabled: stock sync restricted, risk data unreliable */}
                      <TableCell className="text-center">
                        <TooltipProvider delayDuration={200}><Tooltip><TooltipTrigger asChild>
                          <Badge variant="outline" className="rounded-sm text-xs text-muted-foreground border-muted-foreground/30" data-testid={`risk-${item.sku}`}>Not in use</Badge>
                        </TooltipTrigger><TooltipContent className="text-xs">Stock risk is disabled while per-item stock sync is restricted (Zoho quota outage fix)</TooltipContent></Tooltip></TooltipProvider>
                      </TableCell>
                      {/* Aging */}
                      <TableCell className="text-center">
                        {(() => {
                          const age = agingData[item.sku];
                          if (!age) return <span className="text-muted-foreground text-xs">—</span>;
                          return (
                            <TooltipProvider delayDuration={200}><Tooltip><TooltipTrigger asChild>
                              <span className={`inline-flex items-center gap-1 text-xs font-mono ${age.is_old ? 'text-amber-700' : 'text-muted-foreground'}`} data-testid={`aging-${item.sku}`}>
                                {age.is_old && <Clock className="h-3 w-3" />}
                                {age.avg_age_days}d
                                {age.old_stock_pct > 0 && <span className="text-[10px]">({age.old_stock_pct}%)</span>}
                              </span>
                            </TooltipTrigger><TooltipContent className="text-xs max-w-[200px]"><p>Avg age: {age.avg_age_days} days</p><p>Old stock (&gt;90d): {age.old_stock_pct}% ({formatNumber(age.old_stock_qty)} units)</p>{age.is_old && <p className="text-amber-600 font-medium">Warning: &gt;30% stock is old</p>}</TooltipContent></Tooltip></TooltipProvider>
                          );
                        })()}
                      </TableCell>
                      {canAssignItems && (
                        <TableCell>
                          {item.assigned_to_name ? <Badge variant="secondary" className="rounded-sm text-xs"><Users className="h-3 w-3 mr-1" />{item.assigned_to_name}</Badge> : <span className="text-muted-foreground text-xs">—</span>}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          {analyticsLoading && (
            <div className="flex items-center justify-center py-2 bg-muted/30 border-t">
              <Loader2 className="h-4 w-4 animate-spin mr-2 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Loading price analytics...</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {formatNumber(total)} items</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="rounded-sm">Previous</Button>
          <span className="flex items-center px-3 text-sm">Page {page + 1} of {totalPages || 1}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1} className="rounded-sm">Next</Button>
        </div>
      </div>

      <AssignItemsModal open={assignModalOpen} onClose={() => setAssignModalOpen(false)} selectedItems={selectedItems} productManagers={productManagers} selectedPM={selectedPM} setSelectedPM={setSelectedPM} onAssign={handleAssignItems} assigning={assigning} />
      <Suspense fallback={null}>
        {historyModalOpen && <ItemHistoryModal open={historyModalOpen} onClose={() => setHistoryModalOpen(false)} sku={historySku} />}
      </Suspense>

      {/* Unassign Confirmation Dialog */}
      <Dialog open={unassignConfirm} onOpenChange={setUnassignConfirm}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Confirm Unassign</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Are you sure you want to unassign the selected {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} from their assigned Product Manager?</p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setUnassignConfirm(false)} className="rounded-sm">Cancel</Button>
            <Button variant="destructive" onClick={doUnassign} className="rounded-sm" data-testid="confirm-unassign-btn">Unassign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conflict Warning Dialog */}
      <Dialog open={!!conflictData} onOpenChange={() => setConflictData(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Item Already Assigned
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              The following {conflictData?.length || 0} item{(conflictData?.length || 0) !== 1 ? 's are' : ' is'} already assigned:
            </p>
            <div className="border rounded-sm max-h-[200px] overflow-y-auto">
              {conflictData?.map((c, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 text-sm">
                  <span className="font-mono text-xs font-medium">{c.sku}</span>
                  <span className="text-muted-foreground">{c.name}</span>
                  <Badge variant="outline" className="text-xs rounded-sm">{c.current_assignee}</Badge>
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">Do you want to continue with this assignment?</p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConflictData(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => { setConflictData(null); doAssign(); }} className="rounded-sm bg-amber-600 hover:bg-amber-700" data-testid="conflict-continue-btn">
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
